import EditConflictInspector from './EditConflictInspector';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import api from '../services/api';

const BUSINESS_UNITS = [
  'TechSupport', 'Reliability', 'Academy', 'Processlab',
  'SupplyChain', 'Automation', 'IT', 'ESD'
];

const STATUS_OPTIONS = [
  { value: 'planning', label: 'Planning' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

const EMPTY_FORM = {
  name: '',
  description: '',
  company_name: '',
  contact_phone: '',
  contact_email: '',
  contact_person: '',
  requirements: '',
  business_units: [],
  client_id: '',
  status: 'planning',
  priority: 'medium',
  intercompany_sale_enabled: false,
  attachments: []
};

const formatCurrency = (amount, currency = 'MXN') => {
  const numericAmount = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(numericAmount);
};

const ProjectFormDialog = ({
  open,
  onClose,
  editingProject,
  formData,
  error,
  onSave
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [localData, setLocalData] = useState(EMPTY_FORM);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [linkedQuoteBudget, setLinkedQuoteBudget] = useState(null);

  useEffect(() => {
    if (!open) return;

    const nextData = formData
      ? {
        ...EMPTY_FORM,
        ...formData,
        attachments: formData.attachments || [],
        priority: formData.priority || 'medium',
        client_id: formData.client_id || ''
      }
      : EMPTY_FORM;

    setLocalData(nextData);
  }, [open, formData]);

  useEffect(() => {
    if (!open) return;

    const fetchClients = async () => {
      try {
        setLoadingClients(true);
        const response = await api.get('/clients');
        setClients(response.data.clients || response.data || []);
      } catch (error) {
        console.error('Error loading clients:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [open]);

  useEffect(() => {
    if (!open || !editingProject?.id) {
      setLinkedQuoteBudget(null);
      return;
    }

    const fetchLatestProjectQuote = async () => {
      try {
        const response = await api.get(`/projects/${editingProject.id}/latest-quote`);
        const latestQuote = response?.data?.quote || null;

        if (!latestQuote) {
          setLinkedQuoteBudget(null);
          return;
        }

        const numericTotal = parseFloat(latestQuote.total);
        const quoteTotal = Number.isFinite(numericTotal) ? numericTotal : 0;
        const quoteCurrency = (latestQuote.currency || 'MXN').toUpperCase();

        setLinkedQuoteBudget({
          id: latestQuote.id,
          invoiceNumber: latestQuote.invoice_number || `QUOTE-${latestQuote.id}`,
          total: quoteTotal,
          currency: quoteCurrency
        });

      } catch (error) {
        console.error('Error loading linked quote for project budget:', error);
        setLinkedQuoteBudget(null);
      }
    };

    fetchLatestProjectQuote();
  }, [open, editingProject?.id]);

  const selectedClient = useMemo(
    () => clients.find((client) => String(client.id) === String(localData.client_id)) || null,
    [clients, localData.client_id]
  );
  const clientSelectorOptions = useMemo(() => [
    { id: '', company_name: 'None, I will capture it manually', manualEntry: true },
    ...clients
  ], [clients]);
  const selectedClientOption = clientSelectorOptions.find(
    (client) => String(client.id) === String(localData.client_id || '')
  ) || clientSelectorOptions[0];
  const clientOptionLabel = (client) => {
    if (!client) return '';
    if (client.manualEntry) return client.company_name;
    const company = client.company_name || 'Unnamed client';
    const contact = client.contact_name || client.email || 'No contact';
    return `${company} - ${contact}`;
  };

  const isSaveDisabled = !localData.name.trim()
    || !localData.company_name.trim()
    || !Array.isArray(localData.business_units)
    || localData.business_units.length === 0;

  const handleLocalChange = (field, value) => {
    setLocalData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClientSelect = (clientId) => {
    const selected = clients.find((client) => String(client.id) === String(clientId));

    if (!selected) {
      setLocalData((prev) => ({ ...prev, client_id: '' }));
      return;
    }

    setLocalData((prev) => ({
      ...prev,
      client_id: String(clientId),
      company_name: selected.company_name || prev.company_name,
      contact_person: selected.contact_name || prev.contact_person,
      contact_email: selected.email || prev.contact_email,
      contact_phone: selected.phone || prev.contact_phone
    }));
  };

  const handleBusinessUnitToggle = (unit) => {
    setLocalData((prev) => {
      const currentUnits = Array.isArray(prev.business_units) ? prev.business_units : [];
      const exists = currentUnits.includes(unit);
      return {
        ...prev,
        business_units: exists
          ? currentUnits.filter((item) => item !== unit)
          : [...currentUnits, unit]
      };
    });
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setLocalData((prev) => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...files]
    }));

    event.target.value = '';
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setLocalData((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSave = () => {
    const payload = {
      ...localData,
      ...(linkedQuoteBudget ? { budget: linkedQuoteBudget.total } : {})
    };

    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: isMobile ? {
          m: 0,
          width: '100%',
          maxWidth: '100%',
          height: '100dvh',
          maxHeight: '100dvh',
          borderRadius: 0,
          overflow: 'hidden'
        } : undefined
      }}
    >
      <DialogTitle sx={{
        fontWeight: 700,
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: { xs: 2, sm: 3 },
        pt: isMobile ? 'max(16px, env(safe-area-inset-top, 0px))' : 2.5,
        pb: 1.75,
        flexShrink: 0
      }}>
        {editingProject ? 'Edit Project' : 'Create New Project'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Start with the client, project name, company and business unit. The rest can be completed after.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{
        px: { xs: 2, sm: 3 },
        pt: 3,
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}{error && editingProject && <EditConflictInspector resource="projects" baseline={editingProject} localRecord={localData} />}
          <Alert severity="info" variant="outlined">
            Quick flow: select a client if it already exists, confirm the main information, then save the project and continue refining details later.
          </Alert>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Basic setup
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  options={clientSelectorOptions}
                  value={selectedClientOption}
                  onChange={(_event, client) => handleClientSelect(client?.id || '')}
                  getOptionLabel={clientOptionLabel}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  loading={loadingClients}
                  autoHighlight
                  openOnFocus
                  selectOnFocus
                  blurOnSelect
                  noOptionsText="No matching clients"
                  renderOption={(props, client) => (
                    <Box component="li" {...props} key={client.manualEntry ? 'manual-entry' : client.id}>
                      <Box sx={{ minWidth: 0, width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: client.manualEntry ? 500 : 700 }}>
                          {client.company_name || 'Unnamed client'}
                        </Typography>
                        {!client.manualEntry && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {[client.contact_name, client.email].filter(Boolean).join(' · ') || 'No contact'}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  slotProps={{
                    paper: {
                      elevation: 5,
                      sx: {
                        mt: 0.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }
                    },
                    listbox: {
                      sx: {
                        maxHeight: { xs: 'min(46dvh, 360px)', sm: 420 },
                        py: 0.5,
                        '& .MuiAutocomplete-option': {
                          minHeight: 48,
                          mx: 0.75,
                          my: 0.25,
                          px: 1.25,
                          borderRadius: 1.25,
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.35
                        }
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search existing client (Optional)"
                      placeholder="Type a company, contact or email"
                      helperText="Selecting a client auto-fills company and contact data."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <PersonIcon color="action" sx={{ mr: 1, flexShrink: 0 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loadingClients && <CircularProgress color="inherit" size={18} />}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              {selectedClient && (
                <Grid item xs={12}>
                  <Alert icon={<BusinessIcon fontSize="inherit" />} severity="success" variant="outlined" sx={{ overflowWrap: 'anywhere' }}>
                    Linked to <strong>{selectedClient.company_name}</strong>
                    {selectedClient.contact_name ? ` | ${selectedClient.contact_name}` : ''}
                    {selectedClient.email ? ` | ${selectedClient.email}` : ''}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  label="Project Name"
                  value={localData.name || ''}
                  onChange={(event) => handleLocalChange('name', event.target.value)}
                  fullWidth
                  required
                  placeholder="Ex: Inventory Management System"
                  inputProps={{ maxLength: 255 }}
                  helperText={`${(localData.name || '').length}/255 · This is the main name your team will search for.`}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Company Name"
                  value={localData.company_name || ''}
                  onChange={(event) => handleLocalChange('company_name', event.target.value)}
                  fullWidth
                  required
                  placeholder="Ex: Industrial Tech Inc."
                  helperText="Required even if the client is not linked."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Business Units
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                  Select at least one unit. This replaces the old multi-select to make capture faster.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {BUSINESS_UNITS.map((unit) => {
                    const selected = (localData.business_units || []).includes(unit);
                    return (
                      <Chip
                        key={unit}
                        label={unit}
                        clickable
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => handleBusinessUnitToggle(unit)}
                        sx={{ fontWeight: selected ? 700 : 500 }}
                      />
                    );
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Project control
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={localData.status || 'planning'}
                  onChange={(event) => handleLocalChange('status', event.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Priority"
                  value={localData.priority || 'medium'}
                  onChange={(event) => handleLocalChange('priority', event.target.value)}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: '100%',
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Budget source
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {linkedQuoteBudget ? linkedQuoteBudget.invoiceNumber : 'Pending QT assignment'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {linkedQuoteBudget
                      ? `${formatCurrency(linkedQuoteBudget.total, linkedQuoteBudget.currency)} · ${linkedQuoteBudget.status || 'draft'}`
                      : 'The budget will be linked automatically when a quote is assigned to this project.'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper'
                  }}
                >
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(localData.intercompany_sale_enabled)}
                        onChange={(event) => handleLocalChange('intercompany_sale_enabled', event.target.checked)}
                        color="primary"
                      />
                    )}
                    label="Intercompany sale: SMTLINK LLC sold to SMTLINK SA de CV"
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Activalo solo si este proyecto representa una venta interna entre ambas entidades.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Additional details
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Contact information, notes, requirements and optional attachments.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Contact Person"
                    value={localData.contact_person || ''}
                    onChange={(event) => handleLocalChange('contact_person', event.target.value)}
                    fullWidth
                    placeholder="Ex: John Doe - Operations Director"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Contact Phone"
                    value={localData.contact_phone || ''}
                    onChange={(event) => handleLocalChange('contact_phone', event.target.value)}
                    fullWidth
                    placeholder="Ex: +52 123 456 7890"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Contact Email"
                    type="email"
                    value={localData.contact_email || ''}
                    onChange={(event) => handleLocalChange('contact_email', event.target.value)}
                    fullWidth
                    placeholder="contact@company.com"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={localData.description || ''}
                    onChange={(event) => handleLocalChange('description', event.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Briefly describe the project..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Requirements"
                    value={localData.requirements || ''}
                    onChange={(event) => handleLocalChange('requirements', event.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                    placeholder="Describe scope, constraints, deliverables or special requirements..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Add attachments
                    <input
                      hidden
                      type="file"
                      multiple
                      onChange={handleAttachmentChange}
                    />
                  </Button>
                </Grid>

                {(localData.attachments || []).length > 0 && (
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {localData.attachments.map((file, index) => (
                        <Chip
                          key={`${file.name || 'attachment'}-${index}`}
                          label={file.name || `Attachment ${index + 1}`}
                          onDelete={() => handleRemoveAttachment(index)}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 2.5 },
        pt: 1.5,
        pb: isMobile ? 'max(12px, env(safe-area-inset-bottom, 0px))' : 2.5,
        gap: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        '& .MuiButton-root': { minHeight: 44 },
        '& .MuiButton-root + .MuiButton-root': { ml: 0 }
      }}>
        <Button onClick={onClose} sx={{ fontWeight: 600, flex: { xs: 1, sm: 'initial' } }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ fontWeight: 600, px: 3, flex: { xs: 1.4, sm: 'initial' } }}
          disabled={isSaveDisabled}
        >
          {editingProject ? 'Update Project' : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectFormDialog;
